// Production owns these records; Live Staging scopes are never adoption sources.
export const SHARED_TIMEFRAME_SCOPE = 'shared-timeframes-production-v1'
export const PROFILE_TIMEFRAMES = ['1min','2min','5min','15min','30min','65min','daily','weekly']
const PREFIX = 'workspace-chart-indicators-v1'
const LABELS = { position:'Main chart', 'position-lower':'Lower-left chart', 'position-lowerRight':'Lower-right chart', 'position-upperLeft':'Upper-left chart', strategy:'Strategy chart' }
const clone = value => JSON.parse(JSON.stringify(value))
const object = value => value && typeof value === 'object' && !Array.isArray(value)
function mergePatch(base, patch) {
  const next = { ...base }
  for (const [key, value] of Object.entries(patch)) next[key] = object(value) ? mergePatch(base?.[key], value) : value
  return next
}
function changedFields(base, patch) {
  return Object.fromEntries(Object.entries(patch).flatMap(([key, value]) => {
    if (JSON.stringify(base?.[key]) === JSON.stringify(value)) return []
    return [[key, object(value) ? changedFields(base?.[key], value) : value]]
  }))
}
const ANCHOR_STYLE_FIELDS = ['name','color','colorMode','anchorPrice','opacity','lineWidth','lineStyle','priceAxisVisible']
const anchorStyle = value => ({
  // A legacy saved fixed color must not inherit Dynamic from a peer profile.
  ...(Object.hasOwn(value || {}, 'color') ? {colorMode:'fixed'} : {}),
  ...Object.fromEntries(ANCHOR_STYLE_FIELDS
    .filter(key => Object.hasOwn(value || {}, key)).map(key => [key, value[key]])),
})
const DEFAULT_ANCHOR_STYLE = {name:'',color:null,colorMode:'fixed',anchorPrice:null,opacity:100,lineWidth:2,lineStyle:0,priceAxisVisible:true}

// Older profiles may contain an anchor's appearance in only one timeframe.
// Use a saved peer appearance when none exists locally, without rewriting data.
// Explicit local choices win until the next style edit shares that whole style.
export function resolveAnchoredVwapAppearances(profiles, timeframe) {
  const fallback = {}
  for (const tf of PROFILE_TIMEFRAMES) {
    for (const [id, appearance] of Object.entries(profiles[tf]?.anchoredVwap || {})) {
      const style = anchorStyle(appearance)
      if (!fallback[id] && Object.keys(style).length) fallback[id] = style
    }
  }
  const local = profiles[timeframe]?.anchoredVwap || {}
  return Object.fromEntries([...new Set([...Object.keys(fallback), ...Object.keys(local)])]
    .map(id => [id, {
      ...(Object.keys(anchorStyle(local[id])).length ? anchorStyle(local[id]) : fallback[id]),
      ...local[id],
    }]))
}
export function buildLegacyCandidates(templates, preferences) {
  const pref = (scope,key) => preferences.find(row=>row.scope===scope && row.preference_key===key)?.preferences
  const layout=pref('position','workspace-layout-shared-timeframes-production-v1') || pref('position','workspace-layout') || {}
  const shell=pref('position','demo-shell') || {}
  const anchoredVwap = Object.fromEntries(preferences.filter(row => row.scope.startsWith('anchored-vwap:') && row.preference_key === 'appearance')
    .map(row => [row.scope.slice('anchored-vwap:'.length), clone(row.preferences)]))
  const additionalSettings = tf => ({
    relativeStrength: {
      ...Object.fromEntries(['rsAppearance', 'rsColors', 'rsBenchmark', 'rsMargins'].filter(key => layout[key] !== undefined).map(key => [key, clone(layout[key])])),
      rsMarketSession: ['1min','2min','5min','15min','30min'].includes(tf) && layout.rsMarketSession === 'all' ? 'all' : 'regular',
    },
    anchoredVwap: clone(anchoredVwap),
  })
  const candidates={}
  for (const row of templates) {
    if (!row.name?.startsWith(PREFIX)) continue
    const scope=row.name===PREFIX ? 'position' : row.name.slice(PREFIX.length+1)
    // Archive saved strategy variants, but never merge RS/market-health studies.
    if (scope.startsWith('relative-strength') || scope==='market-health') continue
    const slot=scope==='position' ? 'top' : scope.replace('position-','')
    const controls=slot==='top' ? shell : (layout[slot] || {})
    const map=row.preset_snapshots?.timeframes || row.preset_snapshots || {}
    for (const tf of PROFILE_TIMEFRAMES) {
      if (!Array.isArray(map[tf])) continue
      const source={
        ...additionalSettings(tf),
        source:scope, label:LABELS[scope] || (scope === 'qcp-strategy-preview' ? 'Strategy preview' : `Saved strategy chart ${templates.indexOf(row) + 1}`),
        indicators:clone(map[tf]),
        settings:clone((pref(scope,'chart-settings') || pref('position','chart-settings'))?.settings || {}),
        gexStyle:clone(pref(scope,'gex-style') || {}),
        paneRatio:(pref(scope,'volume-pane-ratio') || pref('position','volume-pane-ratio'))?.value ?? .75,
        controls:{
          chartTypesByTheme:Object.fromEntries(['light','dark'].map(theme=>[theme,layout.chartTypesByTheme?.[theme]?.[slot] || controls.chartType || 'bar'])),
          marketSession:['1min','2min','5min','15min','30min'].includes(tf) && controls.marketSession === 'all' ? 'all' : 'regular',
          positionOverlayEnabled:(slot === 'top' ? layout.topPositionOverlayEnabled : controls.positionOverlayEnabled) ?? true,
        },
      }
      ;(candidates[tf] ||= []).push(source)
    }
  }
  // Prefer the pane already displaying this timeframe, then the main chart.
  // Sorting is deterministic across browsers; insert-CAS resolves simultaneous adoption.
  const paneOrder=['position','position-lower','position-lowerRight','position-upperLeft']
  const activeTimeframe=source=>source==='position' ? (layout.topTimeframe || shell.timeframe || 'daily') : layout[source.replace('position-','')]?.timeframe
  for (const [tf,versions] of Object.entries(candidates)) {
    const rank=c=>{
      const index=paneOrder.indexOf(c.source)
      if(index<0)return 100
      return (activeTimeframe(c.source)===tf ? 0 : 10)+index
    }
    versions.sort((a,b)=>rank(a)-rank(b)||a.source.localeCompare(b.source))
  }
  for (const tf of PROFILE_TIMEFRAMES) if (!candidates[tf]?.length) candidates[tf]=[{
    ...additionalSettings(tf),
    source:'empty',label:'Start an empty configuration',indicators:[],settings:{},paneRatio:.75,
    controls:{chartTypesByTheme:{light:'bar',dark:'bar'},marketSession:'regular',positionOverlayEnabled:true},
  }]
  return candidates
}

export function createSharedTimeframeStore(transport, recovery) {
  let state={status:'loading',profiles:{},candidates:{},errors:{},drafts:{},saving:{}}
  let reading=null, disposed=false, legacyLoaded=false
  const listeners=new Set()
  const emit=patch=>{ if(disposed || Object.entries(patch).every(([key,value])=>state[key]===value))return; state={...state,...patch}; if(patch.drafts) { try { recovery?.save({drafts:state.drafts,patches:pendingPatches}) } catch { /* memory retry remains available */ } } listeners.forEach(fn=>fn()) }
  const queued={}, pendingPatches={}
  try {
    const saved=recovery?.load()
    if(saved?.drafts && saved?.patches) {
      state={...state,drafts:saved.drafts}
      Object.assign(pendingPatches,saved.patches)
    }
  } catch { /* unavailable/corrupt local recovery cannot replace server settings */ }
  const write=async(tf,next,revision)=>{
    if(disposed || state.saving[tf])return false
    emit({saving:{...state.saving,[tf]:true},profiles:{...state.profiles,[tf]:next},drafts:{...state.drafts,[tf]:next}})
    try {
      while (true) {
        const ok=await transport.write(tf,next,revision)
        if(disposed)return false
        if(!ok){emit({errors:{...state.errors,[tf]:'conflict'}});return false}
        if(queued[tf]) {
          revision=next.revision
          next=queued[tf]
          delete queued[tf]
          continue
        }
        delete pendingPatches[tf]
        emit({profiles:{...state.profiles,[tf]:next},errors:{...state.errors,[tf]:null},drafts:{...state.drafts,[tf]:null}})
        return true
      }
    }catch{emit({errors:{...state.errors,[tf]:'save_failed'}});return false}
    finally{delete queued[tf];emit({saving:{...state.saving,[tf]:false}})}
  }
  const choose=async(tf,source)=>{
    if(state.status!=='ready' || state.profiles[tf] || state.saving[tf])return false
    const candidate=state.candidates[tf]?.find(item=>item.source===source)
    if(!candidate)return false
    return write(tf,{...clone(candidate),version:1,revision:crypto.randomUUID(),originals:clone(state.candidates[tf])},null)
  }
  const refresh=()=>{
    if(disposed)return Promise.resolve(false)
    if(reading)return reading
    reading=(async()=>{
      try {
        const before=state.profiles
        const profiles=await transport.read()
        const candidates=legacyLoaded ? state.candidates : await transport.legacy()
        if(disposed)return false
        legacyLoaded=true
        // A poll started before a save may finish afterward. The saving flag plus
        // local revision snapshot prevents that old response rolling back a save.
        const next={...profiles}
        for(const tf of Object.keys(next)) if(next[tf]?.revision===state.profiles[tf]?.revision) next[tf]=state.profiles[tf]
        for(const tf of Object.keys(state.saving)) if(state.saving[tf] || before[tf]?.revision!==state.profiles[tf]?.revision) next[tf]=state.profiles[tf]
        const unchanged=Object.keys(next).length===Object.keys(state.profiles).length && Object.keys(next).every(tf=>next[tf]===state.profiles[tf])
        emit({status:'ready',profiles:unchanged ? state.profiles : next,candidates})
        for(const tf of new Set([...Object.keys(candidates),...Object.keys(state.drafts)])) {
          if(state.saving[tf]) continue
          const draft=state.drafts[tf]
          if(draft) {
            if(pendingPatches[tf] && profiles[tf]) {
              // Rebase only locally edited fields onto the latest saved version.
              // Remote changes to other fields survive; no migration controls in the UI.
              await write(tf,{...mergePatch(profiles[tf],pendingPatches[tf]),revision:crypto.randomUUID()},profiles[tf].revision)
            } else if(!profiles[tf]) await write(tf,draft,null)
            else emit({drafts:{...state.drafts,[tf]:null},errors:{...state.errors,[tf]:null}})
          } else if(!state.profiles[tf]) {
            const versions=candidates[tf] || []
            const source=versions[0]
            if(source) await choose(tf,source.source)
          } else {
            // Add missing settings sections to profiles created by the earlier
            // release. Never replace any existing section or legacy source.
            const source = candidates[tf]?.[0]
            const missing = Object.fromEntries(['relativeStrength','anchoredVwap']
              .filter(key => state.profiles[tf][key] === undefined && source?.[key] !== undefined)
              .map(key => [key, clone(source[key])]))
            if (Object.keys(missing).length) {
              const current = state.profiles[tf]
              await write(tf, { ...current, ...missing, revision: crypto.randomUUID() }, current.revision)
            }
          }
        }
        return true
      }catch{emit({status:Object.keys(state.profiles).length?'stale':'unavailable'});return false}
      finally{reading=null}
    })()
    return reading
  }
  const edit=async(tf,patch)=>{
    const profile=state.profiles[tf]
    if(disposed || !profile)return false
    const changes=changedFields(profile,clone(patch))
    if(!Object.keys(changes).length)return true
    pendingPatches[tf]=mergePatch(pendingPatches[tf],changes)
    const next={...mergePatch(profile,changes),revision:crypto.randomUUID()}
    if(state.status!=='ready' || state.errors[tf]) {
      emit({profiles:{...state.profiles,[tf]:next},drafts:{...state.drafts,[tf]:next}})
      return true
    }
    if(state.saving[tf]) {
      queued[tf]=next
      emit({profiles:{...state.profiles,[tf]:next},drafts:{...state.drafts,[tf]:next}})
      return true
    }
    return write(tf,next,profile.revision)
  }
  return {
    getSnapshot:()=>state,
    subscribe:fn=>{listeners.add(fn);return()=>listeners.delete(fn)},
    refresh,choose,edit,
    // Name and style belong to the anchor; visibility remains timeframe-local.
    // Sharing the effective style also repairs older partial saved profiles.
    editAnchoredVwap:async(tf,id,patch,{initialize=false}={})=>{
      if(disposed || !state.profiles[tf])return false
      const hasStyleEdit=ANCHOR_STYLE_FIELDS.some(key=>Object.hasOwn(patch,key))
      const sharedPatch=hasStyleEdit || initialize
        ? {...DEFAULT_ANCHOR_STYLE,...anchorStyle(resolveAnchoredVwapAppearances(state.profiles,tf)[id]),...anchorStyle(patch)} : {}
      const targets=Object.keys(sharedPatch).length
        ? new Set([...Object.keys(state.profiles),...Object.keys(state.candidates)]) : new Set([tf])
      const results=await Promise.all([...targets].map(async target=>{
        if(!state.profiles[target]) {
          const source=state.candidates[target]?.[0]?.source
          if(!source || !(await choose(target,source)))return false
        }
        return edit(target,{anchoredVwap:{[id]:target===tf ? {...sharedPatch,...patch} : sharedPatch}})
      }))
      return results.every(Boolean)
    },
    retry:async(tf)=>{
      const draft=state.drafts[tf]
      if(!draft || state.saving[tf] || !(await refresh()))return false
      if(!state.drafts[tf])return true
      return write(tf,{...draft,revision:crypto.randomUUID()},state.profiles[tf]?.revision ?? null)
    },
    useLatest:tf=>{delete pendingPatches[tf];emit({drafts:{...state.drafts,[tf]:null},errors:{...state.errors,[tf]:null}})},
    dispose:()=>{disposed=true;listeners.clear()},
  }
}
